import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Fallback, Text } from 'v3/components';
import ApplicationLayout from 'v3/layouts/Application';
import { useQuery, useModal, useDatabaseDefinitions, usePermission } from 'hooks';
import { cooperplaceApi } from 'services/api';
import { fetchBodies, fetchImplements, fetchTravelStatus, fetchVehiclesTypes } from 'utils/fetches';
import { useSnackbar } from 'v3/components/Snackbar';
import { DefaultLoadingBodyComponent } from 'v3/components/Card';
import { OccurrencesModal } from 'v3/components/OccurrencesModal';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { filtersMapper, FleetManagementProvider } from './shared';
import FleetManagementTable from './Table';
import Filters from './Filters';
import DrawerColumns from './DrawerColumns';
import NewRegisterModal from './Modals/CreateNewRegister';
import SendEmailsModal from './Modals/SendEmails';
import ErrorsModal from './Modals/Errors';
import DeleteRegisterModal from './Modals/DeleteRegister';
import OutsideTravelModal from './Modals/OutsideTravelModal';
import SuggestionsModal from './Modals/SuggestionsModal';

const DATE_FORMAT = 'DD/MM/YYYY';

const getLast90DaysRange = () => ({
  startDate: moment().subtract(90, 'days').format(DATE_FORMAT),
  endDate: moment().format(DATE_FORMAT),
});

const normalizeDateFilters = (rawFilters = {}) => {
  const fallback = getLast90DaysRange();

  const start = moment(rawFilters?.startDate, DATE_FORMAT, true);
  const end = moment(rawFilters?.endDate, DATE_FORMAT, true);
  const minAllowed = moment().subtract(90, 'days').startOf('day');
  const maxAllowed = moment().endOf('day');

  if (!start.isValid() || !end.isValid()) {
    return { ...rawFilters, ...fallback };
  }

  if (start.isAfter(end)) {
    return { ...rawFilters, ...fallback };
  }

  if (start.isBefore(minAllowed) || end.isAfter(maxAllowed)) {
    return { ...rawFilters, ...fallback };
  }

  if (end.diff(start, 'days') > 90) {
    return { ...rawFilters, ...fallback };
  }

  return {
    ...rawFilters,
    startDate: start.format(DATE_FORMAT),
    endDate: end.format(DATE_FORMAT),
  };
};

function FleetManagement() {
  usePermission('VISUALIZAR_GESTAO_FROTA', { redirect: true });

  const navigate = useNavigate();
  const queryParams = useQuery();

  const occurrencesModal = useModal();
  const [savingNewOccurrence, setSavingNewOccurrence] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [travelId, setTravelId] = useState(null);
  const [bennerTravelId, setBennerTravelId] = useState(null);
  const [fetchingTravelOccurrences, setFetchingTravelOccurrences] = useState(false);

  const [switchTracker, setSwitchTracker] = useState(false);

  const [switchTrackerSuggestion, setSwitchTrackerSuggestion] = useState(false);
  const [switchTrackerUnused, setSwitchTrackerUnused] = useState(false);
  const [switchTrackerAttended, setSwitchTrackerAttended] = useState(false);
  const [switchTrackerExpired, setSwitchTrackerExpired] = useState(false);

  const [isFilterColumnsOpen, setIsFilterColumnsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshingGrid, setRefreshingGrid] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [columns, setColumns] = useState([]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [filtersOptions, setFilterOptions] = useState({
    statusOperator: [],
    aggregation: [
      { value: 'Agregado', label: 'Agregado' },
      { value: 'Terceiro', label: 'Terceiro' },
    ],
    travelStatus: [],
    vehicleTypes: [],
    implementTypes: [],
    bodyTypes: [],
  });

  const [preferenceFilters, setPreferenceFilters] = useState({
    statusOperator: [],
    tags: [],
    operators: [],
    plates: [],
    drivers: [],
    startDate: moment().subtract(90, 'days').format('DD/MM/YYYY'),
    endDate: moment().format('DD/MM/YYYY'),
    startAvailabilityForecast: null,
    endAvailabilityForecast: null,
    aggregation: null,
    branchOffices: [],
    withOccurrences: -1,
    active: -1,
    travelStatus: [],
    vehicleTypes: [],
    implementTypes: [],
    bodyTypes: [],
    fleet: [],
    shipping: [],
  });

  const [suggestionAdvencedFilters, setSuggestionAdvancedFilters] = useState({});
  const [tempSuggestionAdvancedFilters, setTempSuggestionAdvancedFilters] = useState({})
  const [suggestionQtdAdvancedFilters, setSuggestionQtdAdvancedFilters] = useState(0);

  const getDefinitions = async () => {
    try {
      const { data } = await cooperplaceApi.get(
        `/user/definitions?types[]=fleet_management_filters`,
      );
      if (data.length) {
        const parsedFilters = normalizeDateFilters(JSON.parse(data[0].definition));
        setPreferenceFilters((old) => {
          return { ...old, ...parsedFilters };
        });
        return parsedFilters;
      }
      return normalizeDateFilters(preferenceFilters);
    } catch (error) {
      return normalizeDateFilters(preferenceFilters);
    }
  };

  const [filters, setFilters] = useDatabaseDefinitions(
    'fleet_management_filters',
    preferenceFilters,
  );

  const [rowsPerPage, setRowsPerPage] = useState({
    rowsPerPage: 10,
    page: 1,
  });

  const [perPage, setPerPage] = useLocalStorage('fleet_management_per_page', 10);

  const [fetchConfig, setFetchConfig] = useState();
  const [fleetData, setFleetData] = useState([]);

  const [loadSuggestionByVehicles, setLoadSuggestionByVehicle] = useState(false);
  const [validExistImplement, setValidExistImplement] = useState(false);
  const [loadSuggestionType, setLoadSuggestionType] = useState(null);

  const [suggestionData, setSuggestionData] = useState({
    data: [],
    meta: {},
  });

  const [unusedSuggestion, setUnusedSuggestion] = useState({
    data: [],
    meta: {},
  });

  const [attendedSuggestion, setAttendedSuggestion] = useState({
    data: [],
    meta: {},
  });

  const [travelOutside, setTravelOutside] = useState({
    data: [],
    meta: {},
  });
  const [expiredLoads, setExpiredLoads] = useState({
    data: [],
    meta: {},
  });

  const [editedCells, setEditedCells] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [selected, setSelected] = useState(null);
  const snackbar = useSnackbar();
  const newRegisterModal = useModal();
  const sendEmailsModal = useModal();
  const errorsModal = useModal();
  const deleteModal = useModal();
  const newOutsideTravelModal = useModal();
  const outsideTravelModal = useModal();
  const suggestionsModal = useModal();
  const hasMounted = useRef(false);

  const handleCloseFilterColumns = async (newColumns) => {
    setLoading(true);
    try {
      setIsFilterColumnsOpen(false);
      await cooperplaceApi.post(`/user/definitions`, {
        type: 'fleet_management_columns',
        definition: JSON.stringify(newColumns),
      });
      setColumns(newColumns);
    } catch (error) {
      snackbar.show(<Text>Error ao salvar as colunas, tente novamente mais tarde</Text>, {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFilterColumns = () => {
    setIsFilterColumnsOpen(true);
  };

  const handleGetColumns = async () => {
    try {
      const { data: userColumns } = await cooperplaceApi.get('fleet/management/userPreferences');
      return userColumns;
    } catch (error) {
      snackbar.show(<Text>Error ao buscar as colunas, tente novamente mais tarde</Text>, {
        type: 'error',
      });
      return [];
    }
  };

  const verifyFilterAndSetArrayParams = (key, values) => {
    if (values.length) {
      queryParams.set(key, values.join(','));
    } else {
      queryParams.delete(key);
    }
  };

  const verifyFilterAndSetValueParams = (key, value) => {
    if (value) {
      queryParams.set(key, value);
    } else {
      queryParams.delete(key);
    }
  };

  const handleMountQueryParams = () => {
    const { startDate, endDate, startAvailabilityForecast, endAvailabilityForecast } = filters;

    const statusOperator = filters?.statusOperator?.map((status) => status.slug) || [];
    const tags = filters?.tags?.map((tag) => tag.id) || [];
    const plates = filters?.plates?.map((plate) => `${plate.id}-${plate.vehicleType}`);
    const branchOffices = filters?.branchOffices?.map((branch) => branch.id) || [];
    const travelStatus = filters?.travelStatus?.map((status) => status.id) || [];
    const drivers = filters?.drivers?.map((driver) => driver.id) || [];
    const operators = filters?.operators?.map((operator) => operator.id) || [];
    const vehicleTypes = filters?.vehicleTypes?.map((vehicleType) => vehicleType.id) || [];
    const implementTypes = filters?.implementTypes?.map((implementType) => implementType.id) || [];
    const bodyTypes = filters?.bodyTypes?.map((bodyType) => bodyType.id) || [];
    const fleets = filters?.fleet?.map((fleet) => fleet.id) || [];
    const shipping = filters?.shipping?.map((shipping) => shipping.id) || [];

    verifyFilterAndSetArrayParams('statusOperator', statusOperator);
    verifyFilterAndSetArrayParams('tags', tags);
    verifyFilterAndSetArrayParams('plates', plates);
    verifyFilterAndSetValueParams('startDate', startDate);
    verifyFilterAndSetValueParams('endDate', endDate);
    verifyFilterAndSetValueParams('startAvailabilityForecast', startAvailabilityForecast);
    verifyFilterAndSetValueParams('endAvailabilityForecast', endAvailabilityForecast);
    verifyFilterAndSetValueParams('aggregation', filters.aggregation?.value);
    verifyFilterAndSetArrayParams('branchOffices', branchOffices);
    verifyFilterAndSetValueParams('withOccurrences', filters.withOccurrences);
    verifyFilterAndSetValueParams('active', filters.active);
    verifyFilterAndSetArrayParams('travelStatus', travelStatus);
    verifyFilterAndSetArrayParams('drivers', drivers);
    verifyFilterAndSetArrayParams('operators', operators);
    verifyFilterAndSetArrayParams('vehicleTypes', vehicleTypes);
    verifyFilterAndSetArrayParams('implementTypes', implementTypes);
    verifyFilterAndSetArrayParams('bodyTypes', bodyTypes);
    verifyFilterAndSetArrayParams('fleet', fleets);
    verifyFilterAndSetArrayParams('shipping', shipping);

    navigate({ search: queryParams.toString() });
  };

  const handleGetData = async (customFilter) => {
    try {
      if (!{ ...filters, ...customFilter }.startDate || !{ ...filters, ...customFilter }.endDate) {
        throw new Error(
          'Erro ao aplicar filtros! Selecione uma data geral inicial e uma data geral  final',
        );
      }

      if (!customFilter?.not_loading) {
        setLoading(true);
      }

      setRefreshingGrid(true);

      handleMountQueryParams();

      const { data: fleetManagementData } = await cooperplaceApi.get(
        'fleet/management/vehicleManagement',
        {
          params: filtersMapper(
            {
              ...filters,
              ...customFilter,
            },
            {
              page: rowsPerPage.page,
              perPage,
            },
          ),
        },
      );
      setFetchConfig({
        total: fleetManagementData?.meta?.total || 0,
        page: fleetManagementData?.meta?.current_page || 1,
        perPage: fleetManagementData?.meta?.per_page || 10,
      });

      setFleetData(fleetManagementData.data);
      setRefreshingGrid(false);

    } catch (err) {
      const textError = err?.response?.data?.error?.slice(0, 4) || err.message;
      const statusCode = err?.response?.data?.status;

      if (textError === 'DATE') {
        snackbar.show(<Text>{err.response.data.error.replace('DATE', '')}</Text>, {
          type: 'error',
        });
      } else if (
        textError ===
        'Erro ao aplicar filtros! Selecione uma data geral inicial e uma data geral  final'
      ) {
        snackbar.show(<Text>{err.message}</Text>, {
          type: 'error',
        });
      } else if (statusCode === 401) {
        snackbar.show(
          <Text>
            Você não tem permissão para acessar a gestão de otimização de frota e cargas!
          </Text>,
          {
            type: 'error',
          },
        );
      } else {
        snackbar.show(
          <Text>
            Erro ao trazer dados de gestão de otimização de frota e cargas! por favor, recarregue a
            página e tente novamente
          </Text>,
          {
            type: 'error',
          },
        );
      }
    } finally {
      setLoading(false);
      setRefreshingGrid(false);
    }
  };

  const getAvailableStatus = async () => {
    try {
      const { data: statuses } = await cooperplaceApi.get('fleet/management/availableStatus');
      return statuses;
    } catch (error) {
      snackbar.show(<Text>Erro ao buscar opções de status, por favor, recarregue a página</Text>, {
        type: 'error',
      });
      return [];
    }
  };

  const getTravelStatus = async () => {
    try {
      const statuses = await fetchTravelStatus();
      return statuses;
    } catch (error) {
      snackbar.show(
        <Text>Erro ao buscar opções de status da viagem, por favor, recarregue a página</Text>,
        { type: 'error' },
      );
      return [];
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const vehicleTypes = await fetchVehiclesTypes();
      return vehicleTypes;
    } catch (error) {
      return [];
    }
  };

  const fetchImplementTypes = async () => {
    try {
      const implementTypes = await fetchImplements();
      return implementTypes;
    } catch (error) {
      return [];
    }
  };

  const fetchBodyTypes = async () => {
    try {
      const bodyTypes = await fetchBodies();
      return bodyTypes;
    } catch (error) {
      return [];
    }
  };

  const initialFetch = async () => {
    setLoading(true);
    const [
      availableStatus,
      travelStatus,
      vehicleTypes,
      implementTypes,
      bodyTypes,
      userColumns,
      filterDefinitions,
    ] = await Promise.all([
      getAvailableStatus(),
      getTravelStatus(),
      fetchVehicleTypes(),
      fetchImplementTypes(),
      fetchBodyTypes(),
      handleGetColumns(),
      getDefinitions(),
    ]);
    setColumns(userColumns);

    setFilterOptions((old) => ({
      ...old,
      travelStatus,
      statusOperator: availableStatus,
      vehicleTypes,
      implementTypes,
      bodyTypes,
    }));

    const normalizedFilters = normalizeDateFilters(filterDefinitions || preferenceFilters);

    setFilters(normalizedFilters);
    await handleGetData(normalizedFilters);
    setLoading(false);
  };

  useEffect(() => {
    initialFetch();
  }, []);

  useEffect(() => {
    if (hasMounted.current) {
      handleMountQueryParams();
    } else {
      hasMounted.current = true;
    }
  }, [filters]);

  const closeOccurrencesModal = useCallback(async () => {
    if (savingNewOccurrence || loadingUpdate) {
      return;
    }
    try {
      setFetchingTravelOccurrences(true);

      await handleGetData(filters);

      setTravelId(null);
      occurrencesModal.close();
    } catch {
      setTravelId(null);
      occurrencesModal.close();
    } finally {
      setFetchingTravelOccurrences(false);
    }
  }, [travelId, loadingUpdate, savingNewOccurrence]);

  function openOccurrencesModal(clickedTravelId, clickedBennerTravelId) {
    setTravelId(clickedTravelId);
    setBennerTravelId(clickedBennerTravelId);
    setFetchingTravelOccurrences(true);
    occurrencesModal.open();
  }

  const handleSave = useCallback(async () => {
    await handleGetData(filters);
    setEditedCells(null);
    setIsEditing(false);
  }, [handleGetData, filters]);

  return (
    <>
      <FleetManagementProvider
        value={{
          loading,
          setLoading,
          fetching,
          setFetching,
          columns,
          setColumns,
          isFilterColumnsOpen,
          handleCloseFilterColumns,
          handleOpenFilterColumns,
          setFilters,
          filters,
          filtersOptions,
          handleGetData,
          fleetData,
          setFleetData,
          refreshingGrid,
          setRefreshingGrid,
          editedCells,
          setEditedCells,
          isEditing,
          setIsEditing,
          handleSave,
          newRegisterModal,
          sendEmailsModal,
          errorsModal,
          errors,
          setErrors,
          rowsPerPage,
          setRowsPerPage,
          fetchConfig,
          deleteModal,
          setSelected,
          selected,
          outsideTravelModal,
          newOutsideTravelModal,
          suggestionsModal,
          suggestionData,
          setSuggestionData,
          unusedSuggestion,
          setUnusedSuggestion,
          attendedSuggestion,
          setAttendedSuggestion,
          setLoadSuggestionByVehicle,
          loadSuggestionByVehicles,
          loadSuggestionType,
          setLoadSuggestionType,
          setTravelOutside,
          travelOutside,
          setExpiredLoads,
          expiredLoads,
          setValidExistImplement,
          validExistImplement,
          openOccurrencesModal,
          setSwitchTracker,
          switchTracker,
          setSwitchTrackerExpired,
          setSwitchTrackerAttended,
          setSwitchTrackerUnused,
          setSwitchTrackerSuggestion,
          switchTrackerSuggestion,
          switchTrackerUnused,
          switchTrackerAttended,
          switchTrackerExpired,
          setSuggestionAdvancedFilters,
          suggestionAdvencedFilters,
          tempSuggestionAdvancedFilters,
          setTempSuggestionAdvancedFilters,
          setSuggestionQtdAdvancedFilters,
          suggestionQtdAdvancedFilters,
          setIsCreateOpen,
          isCreateOpen,
          setPerPage,
          perPage,
        }}
      >
        <ApplicationLayout title="Gestão de otimização de frota e cargas" contentFluid isOld>
          <Row>
            <Col style={{ marginTop: 30 }}>
              <Fallback fall={columns?.length === 0} component={<DefaultLoadingBodyComponent />}>
                <DrawerColumns />
                <Filters />
                <FleetManagementTable />
                <NewRegisterModal />
                <SendEmailsModal />
                <ErrorsModal />
                <DeleteRegisterModal />
                <OutsideTravelModal />
                <SuggestionsModal />
              </Fallback>
            </Col>
          </Row>
        </ApplicationLayout>
        <OccurrencesModal
          isOpen={occurrencesModal.isOpen}
          onClose={closeOccurrencesModal}
          travelId={travelId}
          bennerTravelId={bennerTravelId}
          fetchingTravelOccurrences={fetchingTravelOccurrences}
          setFetchingTravelOccurrences={setFetchingTravelOccurrences}
          savingNewOccurrence={savingNewOccurrence}
          setSavingNewOccurrence={setSavingNewOccurrence}
          loadingUpdate={loadingUpdate}
          setLoadingUpdate={setLoadingUpdate}
        />
      </FleetManagementProvider>
    </>
  );
}

export default FleetManagement;
